import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { sendEmail } from '../services/emailService';
import { 
  Mail, 
  Phone, 
  MapPin, 
  Clock, 
  Send,
  MessageSquare,
  Users,
  Building2,
  Globe
} from 'lucide-react';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    inquiryType: '',
    subject: '',
    message: '',
    preferredContact: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Show loading message
    toast.loading('Sending your message...');
    
    try {
      const result = await sendEmail(formData, 'Contact Form', 'Contact Page');
      
      if (result.success) {
        toast.success('Message sent successfully! We\'ll respond within 24 hours.');
        setFormData({
          name: '',
          email: '',
          phone: '',
          inquiryType: '',
          subject: '',
          message: '',
          preferredContact: ''
        });
      } else {
        toast.error('Failed to send message. Please try again or contact us directly.');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('Failed to send message. Please try again or contact us directly.');
    }
  };

  const contactInfo = [
    {
      icon: Mail,
      title: "General Inquiries",
      info: "support@vantagepointshospitality.net",
      description: "For general questions and information"
    },
    {
      icon: Users,
      title: "Host Partnership",
      info: "host@vantagepointshospitality.net",
      description: "Property owners and hosting inquiries"
    },
    {
      icon: MessageSquare,
      title: "Guest Support",
      info: "guest@vantagepointshospitality.net",
      description: "Booking assistance and guest services"
    },
    {
      icon: Building2,
      title: "Service Providers",
      info: "service@vantagepointshospitality.net",
      description: "Partnership opportunities for service providers"
    }
  ];

  const offices = [
    {
      country: "United States",
      city: "San Francisco, CA",
      address: "Financial District, San Francisco",
      phone: "+1 7076461714",
      hours: "Mon-Fri: 9AM-6PM PST"
    },
    {
      country: "United Arab Emirates", 
      city: "Dubai",
      address: "Business Bay, Dubai",
      phone: "+971559302690",
      hours: "Sun-Thu: 9AM-6PM GST"
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-teal-600 via-cyan-700 to-blue-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl font-bold mb-6 leading-tight">
              Get In Touch
              <span className="block text-teal-200">We're Here to Help</span>
            </h1>
            <p className="text-xl text-teal-100 max-w-3xl mx-auto leading-relaxed mb-8">
              Have questions about our services? Need assistance with your property or booking? 
              Our global team is ready to provide personalized support.
            </p>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 max-w-2xl mx-auto">
              <p className="text-teal-200 text-sm font-light mb-3">Official Hospitality Partner</p>
              <div className="flex items-center justify-center space-x-4 mb-2">
                <img 
                  src="https://customer-assets.emergentagent.com/job_hostease-global/artifacts/n2vzlpaz_OIA.png" 
                  alt="OIA Green Ventures" 
                  className="h-12 w-auto object-contain bg-white/20 rounded p-2"
                />
                <div className="text-white text-lg font-light">
                  OIA Green Ventures & Portfolio Companies
                </div>
              </div>
              <p className="text-teal-200/70 text-center text-sm font-light">Since 2023</p>
            </div>
            <div className="flex items-center justify-center space-x-8 text-teal-200">
              <div className="flex items-center">
                <Clock className="h-5 w-5 mr-2" />
                <span>24/7 Support</span>
              </div>
              <div className="flex items-center">
                <Globe className="h-5 w-5 mr-2" />
                <span>Global Coverage</span>
              </div>
              <div className="flex items-center">
                <Phone className="h-5 w-5 mr-2" />
                <span>Multi-Language</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Multiple Ways to Reach Us
            </h2>
            <p className="text-xl text-gray-600">
              Choose the best way to connect based on your needs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {contactInfo.map((contact, index) => {
              const IconComponent = contact.icon;
              return (
                <Card key={index} className="text-center hover:shadow-lg transition-shadow duration-300">
                  <CardContent className="p-8">
                    <div className="bg-teal-100 p-4 rounded-full w-fit mx-auto mb-6">
                      <IconComponent className="h-8 w-8 text-teal-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">{contact.title}</h3>
                    <p className="text-teal-600 font-medium mb-2">{contact.info}</p>
                    <p className="text-gray-600 text-sm leading-relaxed">{contact.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Main Content - Form and Office Info */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">
                Send Us a Message
              </h2>
              
              <Card className="shadow-xl border-0">
                <CardHeader className="bg-teal-600 text-white">
                  <CardTitle className="flex items-center">
                    <Send className="mr-2 h-5 w-5" />
                    Contact Form
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-8">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Full Name *
                        </label>
                        <Input
                          required
                          value={formData.name}
                          onChange={(e) => setFormData({...formData, name: e.target.value})}
                          placeholder="Enter your full name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Email Address *
                        </label>
                        <Input
                          type="email"
                          required
                          value={formData.email}
                          onChange={(e) => setFormData({...formData, email: e.target.value})}
                          placeholder="your.email@example.com"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Phone Number
                        </label>
                        <Input
                          value={formData.phone}
                          onChange={(e) => setFormData({...formData, phone: e.target.value})}
                          placeholder="+1 (555) 123-4567"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Inquiry Type *
                        </label>
                        <Select value={formData.inquiryType} onValueChange={(value) => setFormData({...formData, inquiryType: value})}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select inquiry type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="general">General Information</SelectItem>
                            <SelectItem value="hosting">Property Hosting</SelectItem>
                            <SelectItem value="booking">Guest Booking</SelectItem>
                            <SelectItem value="service">Service Partnership</SelectItem>
                            <SelectItem value="support">Technical Support</SelectItem>
                            <SelectItem value="media">Media & Press</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Subject *
                      </label>
                      <Input
                        required
                        value={formData.subject}
                        onChange={(e) => setFormData({...formData, subject: e.target.value})}
                        placeholder="Brief description of your inquiry"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Message *
                      </label>
                      <Textarea
                        required
                        value={formData.message}
                        onChange={(e) => setFormData({...formData, message: e.target.value})}
                        placeholder="Please provide details about your inquiry, questions, or how we can help you..."
                        rows={5}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Preferred Contact Method
                      </label>
                      <Select value={formData.preferredContact} onValueChange={(value) => setFormData({...formData, preferredContact: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="How would you like us to respond?" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="email">Email</SelectItem>
                          <SelectItem value="phone">Phone Call</SelectItem>
                          <SelectItem value="whatsapp">WhatsApp</SelectItem>
                          <SelectItem value="any">Any Method</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <Button type="submit" size="lg" className="w-full bg-teal-600 hover:bg-teal-700">
                      <Send className="mr-2 h-5 w-5" />
                      Send Message
                    </Button>
                  </form>

                  <div className="mt-6 text-center">
                    <p className="text-sm text-gray-600">
                      We typically respond within 24 hours during business days.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Office Information */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">
                Our Global Offices
              </h2>
              
              <div className="space-y-8">
                {offices.map((office, index) => (
                  <Card key={index} className="hover:shadow-lg transition-shadow duration-300">
                    <CardContent className="p-8">
                      <div className="flex items-start space-x-4">
                        <div className="bg-teal-100 p-3 rounded-full">
                          <MapPin className="h-6 w-6 text-teal-600" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-xl font-semibold text-gray-900 mb-1">
                            {office.country}
                          </h3>
                          <p className="text-gray-600 mb-2">{office.city}</p>
                          <p className="text-gray-600 mb-3">{office.address}</p>
                          
                          <div className="space-y-2">
                            <div className="flex items-center text-gray-600">
                              <Phone className="h-4 w-4 mr-2 text-teal-600" />
                              <span className="font-medium">{office.phone}</span>
                            </div>
                            <div className="flex items-center text-gray-600">
                              <Clock className="h-4 w-4 mr-2 text-teal-600" />
                              <span>{office.hours}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <Card className="mt-8 bg-teal-50 border-teal-200">
                <CardContent className="p-8 text-center">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">
                    24/7 Emergency Support
                  </h3>
                  <p className="text-gray-600 mb-4">
                    For urgent guest issues or property emergencies
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-center justify-center">
                      <Phone className="h-5 w-5 mr-2 text-teal-600" />
                      <span className="font-semibold text-lg">+17076461714</span>
                    </div>
                    <p className="text-sm text-gray-500">
                      Available 24/7 for registered guests and property owners
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;